import * as Yup from 'yup';
import { useCallback, useMemo, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
// _mock
import { _tags } from 'src/_mock';
// types
import { HTBaiViet, HTBaiVietApi, HTBaiVietTheLoai } from 'src/types/htblog';
import { blogAPI } from 'src/api/blog-api';
// components
import { CustomFile } from 'src/components/upload';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, {
  RHFEditor,
  RHFUpload,
  RHFTextField,
  RHFAutocomplete,
  RHFSwitch,
} from 'src/components/hook-form';
// hooks
import CustomLoadingButton from 'src/components/button/button-loadding-dark-model';
import { MessCode } from 'src/types/enums';
import useBlog from './hooks/use-blog';
//
import BlogDetailsPreview from './blog-details-preview';

// ----------------------------------------------------------------------

type Props = {
  currentBlog?: HTBaiViet;
};

interface FormValuesProps {
  title: string;
  short_description: string;
  full_description: string;
  coverUrl: CustomFile | string | null;
  categoryId: number;
  is_public: boolean;
}

export default function BlogNewEditForm({ currentBlog }: Props) {
  const router = useRouter();

  const mdUp = useResponsive('up', 'md');

  const { enqueueSnackbar } = useSnackbar();

  const preview = useBoolean();

  const { categories, getCategories } = useBlog();

  const NewBlogSchema = Yup.object().shape({
    title: Yup.string().required('Tiêu đề là bắt buộc'),
    short_description: Yup.string().required('Mô tả ngắn là bắt buộc'),
    full_description: Yup.string().required('Mô tả đầy đủ là bắt buộc'),
    categoryId: Yup.number().required('Thể loại là bắt buộc'),
    coverUrl: Yup.mixed().when([], {
      is: () => !currentBlog,
      then: (schema) => schema.required('Ảnh bìa là bắt buộc'),
      otherwise: (schema) => schema.nullable(),
    }),
    is_public: Yup.boolean(),
  });

  const defaultValues = useMemo(
    () => ({
      slug: currentBlog?.slug || '',
      title: currentBlog?.tieuDe || '',
      short_description: currentBlog?.moTaNgan || '',
      full_description: currentBlog?.noiDung || '',
      coverUrl: currentBlog?.hinhAnh || null,
      categoryId: currentBlog?.theLoaiId || 0,
      is_public: (currentBlog?.trangThai ?? 1) === 1,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentBlog]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(NewBlogSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (currentBlog) {
      reset(defaultValues);
    }
  }, [currentBlog, defaultValues, reset]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  useEffect(() => {
    if (categories.length > 0) {
      if (!currentBlog && values.categoryId === 0) {
        setValue('categoryId', categories[0].id);
      } else if (currentBlog && currentBlog.theLoaiId) {
        setValue('categoryId', currentBlog.theLoaiId);
      }
    }
  }, [categories, currentBlog, setValue, values.categoryId]);

  // Tìm category object để hiển thị trong autocomplete
  const selectedCategory = categories.find((cat) => cat.id === values.categoryId);

  const onSubmit = useCallback(
    async (data: FormValuesProps) => {
      try {
        const model: HTBaiVietApi = {
          id: currentBlog?.id || 0,
          theLoaiId: data.categoryId,
          taiKhoanId: currentBlog?.taiKhoanId || '',
          tieuDe: data.title,
          noiDung: data.full_description,
          moTaNgan: data.short_description,
          ngayTao: currentBlog?.ngayTao || new Date(),
          ngayCapNhat: new Date(),
          trangThai: data.is_public ? 1 : 2,
        };

        // Kết hợp: chỉ gửi file khi có file mới
        const file = data.coverUrl instanceof File ? data.coverUrl : undefined;

        const response = currentBlog
          ? await blogAPI.editBlog(currentBlog.id, model as any, file)
          : await blogAPI.createBlog(model as any, file);

        // 3. Kiểm tra kết quả trả về
        if (response.messCode === MessCode.IsOK) {
          enqueueSnackbar(currentBlog ? 'Cập nhật thành công!' : 'Tạo bài viết thành công!', {
            variant: 'success',
          });
          router.push(paths.dashboard.blog.root);
        } else {
          // Nên hiển thị message từ server nếu có
          enqueueSnackbar(
            response.message || (currentBlog ? 'Cập nhật thất bại!' : 'Tạo thất bại!'),
            { variant: 'error' }
          );
        }
      } catch (error) {
        console.error(error);
      }
    },
    [currentBlog, enqueueSnackbar, router]
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('coverUrl', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const handleRemoveFile = useCallback(() => {
    setValue('coverUrl', null);
  }, [setValue]);

  const renderDetails = (
    <>
      {mdUp && (
        <Grid md={4}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Chi tiết
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Tiêu đề, mô tả ngắn, hình ảnh...
          </Typography>
        </Grid>
      )}

      <Grid xs={12} md={8}>
        <Card>
          {!mdUp && <CardHeader title="Chi tiết" />}

          <Stack spacing={3} sx={{ p: 3 }}>
            <RHFTextField name="title" label="Tiêu đề bài viết" />
            <RHFTextField name="slug" label="Slug" disabled />
            <RHFTextField name="short_description" label="Mô tả ngắn" multiline rows={3} />

            <RHFAutocomplete
              name="categoryId"
              label="Thể loại bài viết"
              options={categories}
              value={selectedCategory || null}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.ten)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, newValue) => {
                setValue('categoryId', newValue ? (newValue as HTBaiVietTheLoai).id : 0);
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.ten}
                </li>
              )}
            />

            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Mô tả đầy đủ</Typography>
              <RHFEditor simple name="full_description" />
            </Stack>

            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Ảnh bìa</Typography>
              <RHFUpload
                name="coverUrl"
                maxSize={3145728}
                onDrop={handleDrop}
                onDelete={handleRemoveFile}
              />
            </Stack>
          </Stack>
        </Card>
      </Grid>
    </>
  );

  const renderProperties = (
    <>
      {mdUp && (
        <Grid md={4}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Thuộc Tính
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Các chức năng và thuộc tính...
          </Typography>
        </Grid>
      )}

      <Grid xs={12} md={8}>
        <Card>
          {!mdUp && <CardHeader title="Thuộc tính" />}

          <Stack spacing={3} sx={{ p: 3 }}>
            <RHFSwitch name="is_public" label="Công bố" />
          </Stack>
        </Card>
      </Grid>
    </>
  );

  const renderActions = (
    <>
      {mdUp && <Grid md={4} />}
      <Grid xs={12} md={8} sx={{ display: 'flex', alignItems: 'center' }}>
        {/* <FormControlLabel
          control={<Switch defaultChecked />}
          label="Publish"
          sx={{ flexGrow: 1, pl: 3 }}
        /> */}

        <Button color="inherit" variant="outlined" size="large" onClick={preview.onTrue}>
          Xem trước
        </Button>

        <CustomLoadingButton
          type="submit"
          variant="contained"
          size="large"
          loading={isSubmitting}
          sx={{ ml: 2 }}
        >
          {!currentBlog ? 'Tạo bài viết' : 'Cập nhật'}
        </CustomLoadingButton>
      </Grid>
    </>
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        {renderDetails}

        {renderProperties}

        {renderActions}
      </Grid>

      <BlogDetailsPreview
        title={values.title}
        content={values.full_description}
        description={values.short_description}
        coverUrl={
          typeof values.coverUrl === 'string' ? values.coverUrl : values.coverUrl?.preview || ''
        }
        //
        open={preview.value}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onClose={preview.onFalse}
        onSubmit={handleSubmit(onSubmit)}
      />
    </FormProvider>
  );
}
